import BaseObject from './base_object';
import DOM, { DomEventSubscription, TimeoutSubscription } from '../utils/dom';
import _ from '../utils/underscore';
import Strings from '../utils/strings';
import Event, { SystemEventSubscription } from '../utils/event';
import ServiceFactory from '../services/service_factory';
import { SYSTEM_EVENTS } from '../events/events';
import { BaseComponentParams, IBaseComponent } from './base_component_interface';
import { IOverlayClasses } from '../services/overlay_manager/overlay_manager_service_interface';
import {TimeIntervalName} from '../../../common/utils/time-interval-names';

export default class BaseComponent extends BaseObject implements IBaseComponent {
    static CLASS = 'BaseComponent';

    protected _selector = 'cgp-component';

    protected serviceFactory: ServiceFactory;

    private _isDirective: boolean;
    private _styleSelectors: any;

    private el: HTMLElement;

    protected innerComponentsRef = new Array<BaseComponent>();
    private _systemEventsSubscriptions = new Array<SystemEventSubscription>();
    private _domEventsSubscriptions = new Array<DomEventSubscription>();
    private _timeoutSubscriptions = new Array<number>();
    private _intervalSubscriptions = new Array<number>();

    /**
     * @constructor
     */
    protected init(params: BaseComponentParams) {
        super.init(params);
        params = params || {} as BaseComponentParams;
        this.serviceFactory = params.serviceFactory || ServiceFactory.create();
        this._isDirective = params.isDirective;
        this._styleSelectors = Object.assign({}, this.getDefaultStyleSelectors(), _.isObject(params.styleSelectors) ? params.styleSelectors : _.safeJSON(params.styleSelectors));

        this.el = this.createRootElement(this.bake(params.innerHtml));

        // Add component root selector.
        this.addClass(this._selector);
        this.addClass(this._getIsolationSelector());
        this._setPropertiesSelectors();

        //AB testing
        const abTestingParams = this.serviceFactory.abTestingService().getAbTestingDataForFeature(this.getClassName());
        if (abTestingParams) {
            this.applyABTestingStyleClass(abTestingParams)
        }
    }

    protected _getIsolationSelector(): string {
        return 'cgp-isolation';
    }


    protected createRootElement(innerHtml: string = ''): HTMLElement {
        return DOM.createElement('div', innerHtml);
    }


    start(): BaseComponent {
        return this;
    }

    getStyleSelectors(): any {
        return this._styleSelectors;
    }

    getDefaultStyleSelectors(): any {
        return {};
    }

    getEl(): HTMLElement {
        return this.el;
    }

    setHTML(html: string): BaseComponent {
        if (!html) {
            this.logger.error('an html to set on the component is not defined');
            return this;
        }

        DOM.html(html, this.el);

        return this;
    }

    render(componentEl): BaseComponent {
        if (!_.isElement(componentEl)) {
            this.logger.error('Could not render not dom element type --> ', typeof componentEl);
            throw new Error();
        }

        // Clean current element html markup.
        DOM.html('', this.el);
        DOM.append(this.el, componentEl);

        return this;
    }


    /**
     * Process html to cgp manipulation html,
     * For example language support or show item support (show remember me show wallet etc..)
     */
    bake(html: string, bindingParams = {}): string {
        if(!html) {
            return html;
        }

        const string = new Strings(html),
            bindingArray = string.getCurlyBracesBinding();


        bindingArray.forEach((bind) => {
            const bindingFunction = this[bind.fn].bind(this);
            if (!bind.fn || !_.isFunction(bindingFunction)) {
                return;
            }
            html = html.replace('{' + bind.to + '}', bindingFunction(bind.value, bindingParams));
        });

        return html;
    }

    _model = {};
    /**
     * Show check if we decide to display a show html function.
     */
    protected show(method: string, bindingParams: object): string {
        const html = this._model['get' + method]();
        return html ? this.bake(html, bindingParams) : '';
    }


    /**
     * Return textual value for specific model parameter.
     */
    protected text(value: string): string {
        const text = this._model[value];
        return text;
    }


    /**
     * Binding function, translate the raw html into locale language.
     */
    protected translate(key: string, bindingParams?: object): string {
        return ServiceFactory.create().languageService().translation(key, bindingParams);
    }

    /**
     * inject the variable and then translate him
     * @param value
     * @param bindingParams
     * @returns {string}
     */
    protected injectAndTranslate(value, bindingParams) {
        const key = this.inject(value, bindingParams);
        return this.translate(key, bindingParams);
    }

    /**
     * Inject string into html.
     */
    protected inject(value, bindingParams) {
        if (!_.isString(value)) {
            value = value.toString();
        }
        //Required by FreePlayVoucherComponentController for NJ - working with old platform
        if (bindingParams[value] === '') {
            return bindingParams[value];
        }

        if (bindingParams[value]) {
            return bindingParams[value];
        }

        return value;
    }


    onAppendedToDOM(): void {
        this.innerComponentsRef.forEach(childComponent => {
            childComponent.onAppendedToDOM();
        });
    }

    getOverlayClasses(): IOverlayClasses {
        return null;
    }


    isDirective(): boolean {
        return this._isDirective;
    }

    protected _setPropertiesSelectors(): void {

        const styleSelectors = this.getStyleSelectors();
        if (!styleSelectors) {
            return;
        }
        Object.keys(styleSelectors).forEach((selectorKey) => {
            this.setClassSelectorToEl(this.getPropertiesStyleMapper()[selectorKey], styleSelectors[selectorKey]);
        });
    }


    /**
     * Add class selector to all specified elements into the component
     */
    protected setClassSelectorToEl(elClass, selector): void {
        if (!elClass) {
            return;
        }
        const contentElements = this.getEl().getElementsByClassName(elClass);
        for (let i = 0; i < contentElements.length; i++) {
            DOM.addClass(contentElements[i], selector);
        }
    }


    /**
     * Get mapper for style selectors
     */
    protected getPropertiesStyleMapper(): any {
        return {};
    }

    /**
     * Get internal component element by its class name
     */
    getDOMElementByClassName(className): Element {
        return this.getEl().getElementsByClassName(className)[0];
    }


    /**
     * Remove all resource that were used by component
     */
    shutDown(): void {
        this._detachSystemEvents();
        this._detachDOMEvents();
        this._clearTimeouts();
        this._shutDownChildComponents();
    }

    protected _detachSystemEvents(): void {
        Event.detachEvents(this._systemEventsSubscriptions);
        this._systemEventsSubscriptions = new Array<SystemEventSubscription>();
    }

    private _detachDOMEvents(): void {
        this._domEventsSubscriptions.forEach(subscription => subscription.remove());
        this._domEventsSubscriptions = [];
    }

    private _shutDownChildComponents(): void {
        this.innerComponentsRef.forEach(comp => {
            comp.shutDown();
        });
        this.innerComponentsRef = [];
    };

    private _clearTimeouts(): void {
        this._timeoutSubscriptions.forEach((ref) => {
            DOM.clearTimeout(ref);
        });

        this._timeoutSubscriptions = [];
    }

    querySelector(selector): Element {
        return this.getEl().querySelector(selector);
    }

    querySelectorAll(selector: string): Element[] {
        const result: any = this.getEl().querySelectorAll(selector);

        return result as Element[];
    }

    appendChildComponent(childComponent: BaseComponent, containerSelector?: string): void {
        if(containerSelector) {
            const parentElement = this.querySelector(containerSelector);
            if(!parentElement) {
                this.throwError('291',`Can't add ${childComponent.getClassName()} component as child to ${this.getClassName()} because ${containerSelector} selector doesn't exists`, {childComponent, containerSelector});
            }

            parentElement.appendChild(childComponent.getEl());
        } else {
            this.getEl().appendChild(childComponent.getEl());
        }

        this.innerComponentsRef.push(childComponent);
    }

    removeChildComponent(childComponent: BaseComponent): void {
        const childComponentIndex = this.innerComponentsRef.findIndex((comp) => childComponent === comp);
        if(childComponentIndex >= 0) {
            DOM.removeElement(this.getEl(), childComponent.getEl());
            this.innerComponentsRef.splice(childComponentIndex, 1);
            childComponent.shutDown();
        }
    }

    addStyle(key: string, value: string): void {
        DOM.addStyle(this.getEl(), key, value);
    }

    removeStyle(key: string): void {
        DOM.removeStyle(this.getEl(), key);
    }

    addClass(className: string): void {
        DOM.addClass(this.getEl(), className);
    }

    hasClass(className): boolean {
        return DOM.hasClass(this.getEl(), className);
    }

    removeClass(className: string): void {
        DOM.removeClass(this.getEl(), className);
    }

    setAttribute(attribute: string , value: string): void {
        DOM.addAttribute(this.getEl(), attribute, value);
    }

    protected setInnerHtml(innerHtml: string, bindingParams = {}): void {
        DOM.html(this.bake(innerHtml, bindingParams), this.getEl());
    }

    protected onDOMEvent(element: Element, eventName: string, handler: EventListener): DomEventSubscription {
        const subscription = DOM.onDOMElementEvent(element, eventName, handler.bind(this));
        this._domEventsSubscriptions.push(subscription);
        return {
            remove: () => {
                subscription.remove();
                const index = this._domEventsSubscriptions.findIndex(item => item === subscription);
                if (index >= 0) {
                    this._domEventsSubscriptions.splice(index, 1);
                }
            }
        }
    }

    protected onRootElementEvent(eventName: string, handler: EventListener): DomEventSubscription {
        return this.onDOMEvent(this.getEl(), eventName, handler);
    }

    protected onChildElementEvent(childElementSelector: string, eventName: string, handler: EventListener): DomEventSubscription {
        return this.onDOMEvent(this.querySelector(childElementSelector), eventName, handler);
    }

    protected onSystemEvent(eventName: string | string[], callback: Function): SystemEventSubscription {
        const subscription = Event.on(eventName, callback.bind(this), this);
        this._systemEventsSubscriptions.push(subscription);
        return this._createEventSubscription(subscription);
    }

    protected onceSystemEvent(eventName: string, callback: Function): SystemEventSubscription {
        const subscription = Event.once(eventName, callback, this);
        this._systemEventsSubscriptions.push(subscription);
        return this._createEventSubscription(subscription);
    }

    private _createEventSubscription(subscription: SystemEventSubscription): SystemEventSubscription {
        return {
            off: () => {
                subscription.off();
                const index = this._systemEventsSubscriptions.findIndex(item => item === subscription);
                if (index >= 0) {
                    this._systemEventsSubscriptions.splice(index, 1);
                }
            }
        }
    }

    setInterval(callback: () => void, timeout: number, name?: TimeIntervalName): TimeoutSubscription {
        const timeoutRef = DOM.setInterval(callback, timeout, name);
        this._intervalSubscriptions.push(timeoutRef);

        return {
            clear: () => {
                clearInterval(timeoutRef);
                const indexOfTimeoutRef = this._intervalSubscriptions.findIndex(item => item === timeoutRef);
                if (indexOfTimeoutRef >= 0) {
                    this._timeoutSubscriptions.splice(indexOfTimeoutRef, 1);
                }
            }
        };
    }

    setTimeout(callback: () => void, timeout: number, name?: TimeIntervalName): TimeoutSubscription {
        const timeoutRef = DOM.setTimeout(callback, timeout, name);
        this._timeoutSubscriptions.push(timeoutRef);
        return {
            clear: () => {
                DOM.clearTimeout(timeoutRef);

                const indexOfTimeoutRef = this._timeoutSubscriptions.findIndex(item => item === timeoutRef);
                if (indexOfTimeoutRef >= 0) {
                    this._timeoutSubscriptions.splice(indexOfTimeoutRef, 1);
                }
            }
        };
    }

    _sendAnalytics(analyticsData) {
        const defaultAnalyticsData = {
            category: '[Technical-event]',
        };

        Event.fire(SYSTEM_EVENTS.ANALYTIC_TRIGGER_ACTIVATED, Object.assign(defaultAnalyticsData, analyticsData));
    }

    hideRootElement(): void {
        DOM.hide(this.getEl());
    }

    showRootElement(): void {
        DOM.show(this.getEl());
    }

    applyABTestingStyleClass(properties: any): void {
        if (properties.Properties){
            if (properties.Properties['css-selector']) {
                DOM.addClass(this.getEl(), 'cgp-ab-' + properties.Properties['css-selector']);
            }
        }
    }
}